import { Subject } from 'rxjs';
import WaveformData from 'waveform-data';

import { AudioEditor } from './AudioEditor';
import { peach, roundRect } from './Draw';
import Waveform from './Waveform';

function scale(y: number, height: number, padding: number) {
  return ((128 - y) / 256) * (height - padding) + padding / 2;
}

const SPACE_BOTTOM = 14;
const SPACE_TOP = 1;

export default class OverviewWaveform extends Waveform {
  constructor(
    audioEditor: AudioEditor,
    private readonly viewDraggingSubject: Subject<{ from: number; to: number }>,
  ) {
    super(audioEditor, audioEditor.options.overview);
    // this.ctx.canvas.style.borderRadius = '3px';
    this.ctx.canvas.style.cursor = 'pointer';

    let hasClicked = false;

    this.ctx.canvas.addEventListener('click', event => {
      this.moveCursor(event);
    });

    this.ctx.canvas.addEventListener('pointerdown', event => {
      event.preventDefault();
      this.ctx.canvas.setPointerCapture(event.pointerId);
      hasClicked = true;
    });

    this.ctx.canvas.addEventListener('pointerup', event => {
      event.preventDefault();
      hasClicked = false;
    });

    this.ctx.canvas.addEventListener('pointermove', event => {
      event.preventDefault();
      if (!hasClicked) {
        return;
      }
      this.moveCursor(event);
    });

    audioEditor.onViewDragged$.subscribe(({ from, to }) => {
      super.refresh();
      this.drawViewBox(from, to);
    });
  }

  private moveCursor(event: MouseEvent) {
    const time =
      (event.offsetX / this.ctx.canvas.width) * this.audioEditor.waveformData.duration;
    if (!this.audioEditor.player.isPlaying) {
      this.audioEditor.player.setTime(time);
    }

    const duration = this.audioEditor.viewBox.to - this.audioEditor.viewBox.from;
    const from = time - duration / 2;
    const to = time + duration / 2;

    this.viewDraggingSubject.next({ from, to });
  }

  moveToStart() {
    const to = this.audioEditor.viewBox.to - this.audioEditor.viewBox.from;
    this.viewDraggingSubject.next({ from: 0, to });
  }

  refresh() {
    if (!this.data) {
      return;
    }
    super.refresh();
    this.drawViewBox(this.audioEditor.viewBox.from, this.audioEditor.viewBox.to);
  }

  drawViewBox(from: number, to: number) {
    // this.ctx.clearRect(0, 0, this.ctx.canvas.width, 5);
    // this.ctx.clearRect(0, this.ctx.canvas.height - 5, this.ctx.canvas.width, 5);

    const x1 = this.timeToPixels(from);
    const x2 = this.timeToPixels(to);

    this.ctx.globalAlpha = 0.35;
    this.ctx.fillStyle = this.audioEditor.options.viewBox.backgroundColor;
    this.ctx.fillRect(x1, 0, x2 - x1, this.ctx.canvas.height - SPACE_BOTTOM);

    this.ctx.globalAlpha = 1;
    this.ctx.fillStyle = this.audioEditor.options.viewBox.handlesColor;
    roundRect(this.ctx, x1 - 2.5, 0, 5, this.ctx.canvas.height - SPACE_BOTTOM, {
      tl: 5,
      tr: 0,
      bl: 5,
      br: 0,
    });
    this.ctx.fill();

    roundRect(this.ctx, x2 - 2.5, 0, 5, this.ctx.canvas.height - SPACE_BOTTOM, {
      tl: 0,
      tr: 5,
      bl: 0,
      br: 5,
    });
    this.ctx.fill();
  }

  drawBackground() {
    this.ctx.fillStyle = this.audioEditor.options.backgroundColor;
    roundRect(
      this.ctx,
      0,
      0,
      this.ctx.canvas.width,
      this.ctx.canvas.height - SPACE_BOTTOM,
      {
        tl: 0,
        tr: 0,
        bl: 0,
        br: 0,
      },
    );
    this.ctx.fill();
  }

  drawWaveform(
    ctx: CanvasRenderingContext2D,
    waveform: WaveformData,
    height: number,
    padding: number,
  ) {
    if (!waveform) {
      return;
    }
    ctx.beginPath();
    ctx.fillStyle = this.audioEditor.options.waveformColor;

    const channel = waveform.channel(0);
    for (let i = 0; i < waveform.length; i += 1) {
      try {
        const y = i >= 0 && i < waveform.length ? channel.min_sample(i) : 0;
        ctx.lineTo(i + 0.5, scale(y, height - SPACE_BOTTOM, padding));
      } catch (e) {}
    }

    for (let i = waveform.length - 1; i >= 0; i -= 1) {
      try {
        const y = i >= 0 && i < waveform.length ? channel.max_sample(i) : 0;
        ctx.lineTo(i + 0.5, scale(y, height - SPACE_BOTTOM, padding));
      } catch (e) {}
    }

    ctx.closePath();
    ctx.fill();
  }

  drawCursor(ctx: CanvasRenderingContext2D, instant: number) {
    const width = this.ctx.canvas.width;
    const x = (instant / this.audioEditor.waveformData.duration) * width;

    ctx.strokeStyle = this.audioEditor.options.cursorColor;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, this.ctx.canvas.height - SPACE_BOTTOM);
    ctx.stroke();
    ctx.lineWidth = 1;
  }

  drawSelectionBox(ctx: CanvasRenderingContext2D) {
    const x1 = this.timeToPixels(this.audioEditor.selectionBox.from);
    const x2 = this.timeToPixels(this.audioEditor.selectionBox.to);

    ctx.beginPath();
    ctx.fillStyle = this.audioEditor.options.selectionBox.waveformColor;
    ctx.globalAlpha = 1;

    const channel = this.data.channel(0);
    for (let i = x1; i < x2; i += 1) {
      try {
        const y = i >= 0 && i < this.data.length ? channel.min_sample(i) : 0;
        ctx.lineTo(
          i + 0.5,
          scale(y, ctx.canvas.height - SPACE_BOTTOM, this.waveformOptions.padding),
        );
      } catch (e) {}
    }

    for (let i = x2 - 1; i >= x1; i -= 1) {
      try {
        const y = i >= 0 && i < this.data.length ? channel.max_sample(i) : 0;
        ctx.lineTo(
          i + 0.5,
          scale(y, ctx.canvas.height - SPACE_BOTTOM, this.waveformOptions.padding),
        );
      } catch (e) {}
    }

    ctx.closePath();
    ctx.fill();
  }

  drawIntroCursor(ctx: CanvasRenderingContext2D, instant: number): void {
    const x = this.timeToPixels(instant);
    if (x > ctx.canvas.width) {
      return;
    }

    ctx.strokeStyle = this.audioEditor.options.intro.color;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(x, SPACE_TOP);
    ctx.lineTo(x, ctx.canvas.height - SPACE_BOTTOM);
    ctx.stroke();
    ctx.lineWidth = 1;

    peach(
      ctx,
      x,
      ctx.canvas.height - SPACE_BOTTOM,
      this.audioEditor.options.intro.color,
      true,
    );
  }

  drawMixCursor(ctx: CanvasRenderingContext2D, instant: number): void {
    const x = this.timeToPixels(instant);
    if (x > ctx.canvas.width) {
      return;
    }

    ctx.strokeStyle = this.audioEditor.options.mix.color;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(x, SPACE_TOP);
    ctx.lineTo(x, ctx.canvas.height - SPACE_BOTTOM);
    ctx.stroke();
    ctx.lineWidth = 1;

    peach(
      ctx,
      x,
      ctx.canvas.height - SPACE_BOTTOM,
      this.audioEditor.options.mix.color,
      true,
    );
  }

  drawOutroCursor(ctx: CanvasRenderingContext2D, instant: number): void {
    const x = this.timeToPixels(instant);
    if (x > ctx.canvas.width) {
      return;
    }

    ctx.strokeStyle = this.audioEditor.options.outro.color;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(x, SPACE_TOP);
    ctx.lineTo(x, ctx.canvas.height - SPACE_BOTTOM);
    ctx.stroke();
    ctx.lineWidth = 1;

    peach(
      ctx,
      x,
      ctx.canvas.height - SPACE_BOTTOM,
      this.audioEditor.options.outro.color,
      true,
    );
  }
}
